<template>
  <div
    v-if="!otpFlags.otpVerified"
    class="otp-section"
  >
    <div
      v-if="!otpFlags.otpSent && !hideGenerateButton"
      class="otp-section__generate"
    >
      <BlueButton
        class="otp-section__generate-btn"
        :is-disabled="false"
        :is-loading="isOtpSending"
        @click="onOpenOtpDirection"
      >
        Згенерувати код
      </BlueButton>
    </div>

    <div
      v-else-if="!otpFlags.otpSent && hideGenerateButton"
      class="otp-section__resend"
    >
      <span>Не отримали код?</span>
      <a
        href="javascript:void(0)"
        class="otp-section__resend-link"
        @click.prevent="onOpenOtpDirection"
      >Відправити код</a>
    </div>

    <div
      v-if="otpFlags.otpSent"
      class="otp-section__form"
    >
      <template v-if="!otpAdditionalCodes || Object.keys(otpAdditionalCodes).length === 0">
        <div class="otp-section__row">
          <p
            v-if="otpSendingError"
            class="otp-section__hint otp-section__hint--error"
          >
            Виникла помилка при відправленні ОТП коду. Спробуйте ще раз.
          </p>
          <p
            v-else-if="otpCheckError"
            class="otp-section__hint otp-section__hint--error"
          >
            Введено невірний код. Спробуйте ще раз.
          </p>
          <p
            v-else
            class="otp-section__hint otp-section__hint--success"
          >
            Код успішно відправлено
          </p>
        </div>
        <div class="otp-section__row otp-section__field">
          <LabeledTextInput
            class="otp-section__input"
            v-model="otpCode"
            :is-disabled="otpFlags.otpVerified || isOtpChecking"
            label="Введіть код"
            placeholder="Введіть код"
            @input="onOtpCodeInput"
          />
          <AccentButton
            class="otp-section__submit-btn"
            :is-disabled="!otpCode || isOtpChecking"
            :is-loading="isOtpChecking"
            @click="onCheckOtp"
          >
            Підтвердити
            <OpenDownIcon
              class="otp-section__submit-btn--icon"
              color="#FFFFFF"
            />
          </AccentButton>
        </div>
      </template>
      <div
        v-else
        class="otp-section__codes"
      >
        <div class="otp-section__row">
          <p
            v-if="otpSendingError"
            class="otp-section__hint otp-section__hint--error"
          >
            Виникла помилка при відправленні ОТП коду. Спробуйте ще раз.
          </p>
          <p
            v-else-if="otpCheckError"
            class="otp-section__hint otp-section__hint--error"
          >
            Введено невірний код. Спробуйте ще раз.
          </p>
          <p
            v-else
            class="otp-section__hint otp-section__hint--success"
          >
            Код успішно відправлено
          </p>
        </div>
        <div class="otp-section__row otp-section__field">
          <LabeledTextInput
            class="otp-section__input"
            v-model="otpCode"
            :is-disabled="otpFlags.otpVerified || isOtpChecking"
            label="Код ОСЦПВ"
            placeholder="Введіть код"
            @input="onOtpCodeInput"
          />
          <AccentButton
            class="otp-section__submit-btn"
            :is-disabled="!otpCode || !isAllOtpCodesEntered || isOtpChecking"
            :is-loading="isOtpChecking"
            @click="onCheckOtp"
          >
            Підтвердити
            <OpenDownIcon
              class="otp-section__submit-btn--icon"
              color="#FFFFFF"
            />
          </AccentButton>
        </div>
        <div
          v-for="(codeValue, codeKey) in otpAdditionalCodes"
          :key="codeKey"
          class="otp-section__row otp-section__field"
        >
          <LabeledTextInput
            class="otp-section__input"
            v-model="otpAdditionalCodeInputs[codeKey]"
            :is-disabled="otpFlags.otpVerified || isOtpChecking"
            :label="`Код ${codeKey === 'cross' ? 'ДЦВ' : codeKey}`"
            placeholder="Введіть код"
            @input="onOtpCodeInput"
          />
          <AccentButton
            class="otp-section__submit-btn"
            :is-disabled="!otpCode || !isAllOtpCodesEntered || isOtpChecking"
            :is-loading="isOtpChecking"
            @click="onCheckOtp"
          >
            Підтвердити
            <OpenDownIcon
              class="otp-section__submit-btn--icon"
              color="#FFFFFF"
            />
          </AccentButton>
        </div>
      </div>

      <div v-if="!otpFlags.otpVerified" class="otp-section__resend">
        <span>Не отримали код?</span>
        <a
          href="javascript:void()"
          class="otp-section__resend-link"
          @click.prevent="onOpenOtpDirection"
        >Відправити повторно</a>
      </div>
    </div>

    <OtpDirectionPopup
      ref="otpDirectionPopup"
      @send="onSendOtp"
    />
  </div>
</template>

<script>
import { AccentButton, BlueButton, LabeledTextInput } from '@/shared/ui/form';
import { OpenDownIcon } from '@/shared/ui/icons';
import { default as OtpDirectionPopup } from '@/shared/ui/insurance/otp-direction-popup';

export default {
  components: { BlueButton, AccentButton, LabeledTextInput, OtpDirectionPopup, OpenDownIcon },
  props: {
    orderId: { type: [String, Number], default: null },
    requiresOtp: { type: Boolean, default: false },
    otpFlags: { type: Object, required: true },
    isOtpSending: { type: Boolean, default: false },
    otpSendingError: { type: Boolean, default: false },
    isOtpChecking: { type: Boolean, default: false },
    otpCheckError: { type: Boolean, default: false },
    otpAdditionalCodes: { type: Object, default: null },
    hideGenerateButton: { type: Boolean, default: false },
  },
  data() {
    return {
      otpCode: '',
      otpAdditionalCodeInputs: {},
    };
  },
  computed: {
    isAllOtpCodesEntered() {
      if (!this.otpCode) return false;
      const additionalKeys = Object.keys(this.otpAdditionalCodes || {});
      if (additionalKeys.length > 0) {
        return additionalKeys.every(key => !!this.otpAdditionalCodeInputs[key]);
      }
      return true;
    },
  },
  watch: {
    'otpFlags.otpVerified'(val) {
      if (val) {
        this.$emit('clear-otp-check-error');
      }
    },
    isOtpSending(val) {
      if (val) {
        this.otpCode = '';
        this.otpAdditionalCodeInputs = {};
      }
    },
    orderId(val) {
      if (val) {
        this.subscribeOtpBroadcast();
      }
    },
  },
  mounted() {
    if (this.orderId) {
      this.subscribeOtpBroadcast();
    }
  },
  beforeDestroy() {
    this.unsubscribeOtpBroadcast();
  },
  methods: {
    onOpenOtpDirection() {
      this.$nextTick(() => {
        if (this.$refs.otpDirectionPopup && typeof this.$refs.otpDirectionPopup.open === 'function') {
          this.$refs.otpDirectionPopup.open();
        } else {
          setTimeout(() => {
            this.$refs.otpDirectionPopup?.open();
          }, 150);
        }
      });
    },
    onSendOtp({ type, commissionPaidBy }) {
      this.$emit('send-otp', { type, commissionPaidBy });
    },
    onOtpCodeInput() {
      this.$emit('clear-otp-check-error');
    },
    onCheckOtp() {
      this.$emit('check-otp', {
        otp: this.otpCode,
        additionalOtps: { ...this.otpAdditionalCodeInputs },
      });
    },
    subscribeOtpBroadcast() {
      if (!this.orderId) return;

      this.$broadcastClient
        .private(`orders.${this.orderId}`)
        .listen('.OtpEntered', () => {
          this.$emit('verified');
        });
    },
    unsubscribeOtpBroadcast() {
      if (!this.orderId) return;

      this.$broadcastClient.leaveChannel(`orders.${this.orderId}`);
    },
  },
};
</script>

<style scoped lang="scss">
.otp-section {
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }

  &__form {
    border: 1px solid #CBD5E1;
    border-radius: 8px;
    padding: 16px;
  }

  &__hint {
    font-family: 'Geist', sans-serif;
    font-size: 14px;
    margin: 0;

    &--success {
      color: #2AA63C;
    }

    &--error {
      color: #FF3B30;
    }
  }

  &__generate {
    display: flex;
    gap: 8px;
    margin-top: 8px;

    &-btn {
      padding: 15px 115px;

      @media (max-width: 768px) {
        width: 100%;
        padding: 15px 24px;
      }
    }
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__field {
    position: relative;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }
  }

  &__input {
    width: 100%;
  }

  &__submit-btn {
    position: absolute;
    right: 0;
    bottom: 4px;
    padding: 7px 16px;

    @media (max-width: 768px) {
      position: static;
      width: 100%;
      justify-content: center;
      padding: 10px 16px;
    }

    &--icon {
      transform: rotate(-90deg);
      margin-left: 4px;
    }
  }

  &__codes {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__resend {
    font-family: 'Geist', sans-serif;
    font-size: 13px;
    color: #71717a;
    margin-top: 12px;

    @media (max-width: 768px) {
      text-align: center;
    }
  }

  &__resend-link {
    color: #0A77FF;
    font-size: 13px;
    font-weight: 500;
    text-decoration: underline;
    padding: 0;
    margin-left: 4px;
  }
}
</style>